import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import GitHub from '@mui/icons-material/GitHub';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { BarrasComparacao } from '@/components/graficos/BarrasComparacao';
import { Composicao } from '@/components/graficos/Composicao';
import { FaixasTurma } from '@/components/graficos/FaixasTurma';
import { Histograma } from '@/components/graficos/Histograma';
import { fmt, fmtPct, situacao } from '@/features/notas/calculo';
import { CriteriosView, TabelaOrigem } from '@/features/notas/correcao';
import { Barramento } from '@/features/temas/Barramento';
import { CuboOlap } from '@/features/temas/CuboOlap';
import { FichaHeroina } from '@/features/temas/FichaHeroina';
import { RolesPerto } from '@/features/temas/RolesPerto';
import {
  AvisoVendoComo,
  CarregandoPainel,
  CartaoInsight,
  ChipSituacaoP1,
  Destaque,
} from '@/features/notas/componentes';
import { insightsDisciplina } from '@/features/notas/insights';
import { Simulador } from '@/features/notas/Simulador';
import { ROTULOS, type Componente, type DisciplinaDesempenho } from '@/features/notas/types';
import { useDesempenho } from '@/features/notas/useDesempenho';

type Aba = 'resumo' | 'tema' | 'aws' | 'prova' | 'quanto-falta' | 'calculo';

/** Visualização no tema da prova de cada disciplina. */
const TEMA: Record<string, string> = {
  PBDI: 'Cubo OLAP',
  POO: 'Ficha da Heroína',
  PAOO: 'Barramento de eventos',
  PDMT: 'Rolês perto da nota',
  PDMN: 'Rolês perto da nota',
};

export function DisciplinaPage() {
  const { codigo, ra } = useParams();
  const [params] = useSearchParams();
  const { data, error, loading } = useDesempenho(ra, params.get('semestre') ?? undefined);
  const [aba, setAba] = useState<Aba>('resumo');
  const voltar = `${ra ? `/admin/aluno/${ra}` : '/'}${data?.semestre ? `?semestre=${data.semestre}` : ''}`;

  if (loading && !data) return <CarregandoPainel />;
  if (error || !data)
    return <Alert severity="error">Não consegui carregar as suas notas agora.</Alert>;
  const d = data.disciplinas.find((x) => x.codigo === codigo);
  if (!d) return <Alert severity="warning">Você não está nesta disciplina.</Alert>;
  if (d.somenteNotas)
    return (
      <Encerrada
        d={d}
        voltar={voltar}
        semestre={data.semestre}
        vendoComo={ra ? data.aluno.nome : null}
      />
    );

  return (
    <>
      {ra && <AvisoVendoComo nome={data.aluno.nome} />}
      <Button
        component={RouterLink}
        to={voltar}
        startIcon={<ArrowBackRounded />}
        sx={{ mb: 1, ml: -1 }}
      >
        Todas as disciplinas
      </Button>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', mb: 2 }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            {d.codigo} · {d.turma} · {d.periodo}
          </Typography>
          <Typography variant="h4" component="h1">
            {d.nome}
          </Typography>
          <Typography color="text.secondary">P1: {d.provaP1}</Typography>
        </Box>
        <Box sx={{ textAlign: { sm: 'right' } }}>
          <Typography variant="caption" color="text.secondary">
            Sua P1
          </Typography>
          <Typography
            variant="h2"
            component="p"
            sx={{ lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
          >
            {fmt(d.notas.P1, 1)}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <ChipSituacaoP1 p1={d.notas.P1} />
          </Box>
        </Box>
      </Stack>

      <Tabs
        value={aba}
        onChange={(_, v: Aba) => setAba(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="resumo" label="Resumo" />
        {TEMA[d.codigo] && <Tab value="tema" label={`✨ ${TEMA[d.codigo]}`} />}
        <Tab value="aws" label="AWS (T1)" />
        <Tab value="prova" label="Prova P1" />
        <Tab value="quanto-falta" label="Quanto falta" />
        <Tab value="calculo" label="Como é calculado" />
      </Tabs>

      {aba === 'resumo' && <Resumo d={d} />}
      {aba === 'tema' && d.codigo === 'PBDI' && <CuboOlap d={d} />}
      {aba === 'tema' && d.codigo === 'POO' && <FichaHeroina d={d} nome={data.aluno.nome} />}
      {aba === 'tema' && d.codigo === 'PAOO' && <Barramento d={d} />}
      {aba === 'tema' && (d.codigo === 'PDMT' || d.codigo === 'PDMN') && <RolesPerto d={d} />}
      {aba === 'aws' && <Aws d={d} />}
      {aba === 'prova' && <Prova d={d} />}
      {aba === 'quanto-falta' && <Simulador d={d} />}
      {aba === 'calculo' && <Calculo d={d} />}
    </>
  );
}

function Resumo({ d }: { d: DisciplinaDesempenho }) {
  const comp = d.turmaInfo.componentes;
  const partes: Componente[] = ['T1', 'IND1', 'GRU1', 'P1'];
  const linhas = partes
    .filter((c) => comp[c])
    .map((c) => ({
      rotulo: c === 'P1' ? 'P1 (final)' : ROTULOS[c].longo.replace(' da P1', ''),
      estat: comp[c]!,
    }));
  const p1 = comp.P1;
  const insights = insightsDisciplina(d);
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Posição na turma"
            valor={p1?.percentil == null ? '–' : `${Math.round(p1.percentil)}%`}
            rodape="da turma ficou abaixo de você"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Média da turma na P1"
            valor={fmt(p1?.media)}
            rodape={p1 ? `mediana ${fmt(p1.mediana)} · ${p1.n} alunos` : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Sua distância da média"
            valor={
              p1 && typeof d.notas.P1 === 'number'
                ? `${d.notas.P1 >= p1.media ? '+' : '−'}${fmt(Math.abs(d.notas.P1 - p1.media))}`
                : '–'
            }
            rodape="pontos em relação à média da turma"
          />
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            De onde veio a sua P1
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            P1 = 0,3 × T1 + 0,5 × individual + 0,2 × grupo. Cada cor é o quanto aquela parte somou;
            o resto da barra é o que ficou de fora.
          </Typography>
          <Composicao
            rotulo="Composição da P1"
            partes={[
              { rotulo: 'AWS (T1)', peso: 0.3, nota: d.notas.T1 ?? null },
              { rotulo: 'Individual', peso: 0.5, nota: d.notas.IND1 ?? null },
              { rotulo: 'Grupo', peso: 0.2, nota: d.notas.GRU1 ?? null },
            ]}
          />
        </CardContent>
      </Card>

      {linhas.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2">
              Você e a turma, parte por parte
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O ponto é você; a faixa cinza é onde ficou a metade do meio da turma; o traço é a
              mediana. Passe o mouse para ver os números.
            </Typography>
            <FaixasTurma linhas={linhas} />
          </CardContent>
        </Card>
      )}

      {p1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2">
              Como a turma se distribuiu na P1
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Quantos alunos tiraram cada faixa de nota. A sua faixa está destacada.
            </Typography>
            <Histograma estat={p1} rotulo="P1" />
          </CardContent>
        </Card>
      )}

      {insights.length > 0 && (
        <Grid container spacing={2}>
          {insights.map((i, k) => (
            <Grid key={k} size={{ xs: 12, sm: 6, md: 4 }}>
              <CartaoInsight insight={i} />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}

function Aws({ d }: { d: DisciplinaDesempenho }) {
  if (!d.awsDisponivel) {
    return (
      <Alert severity="info">
        O relatório do AWS Academy desta turma ainda não foi importado. A T1 aparece aqui assim que
        o professor atualizar.
      </Alert>
    );
  }
  const t1 = d.turmaInfo.componentes.T1;
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque rotulo="Sua T1" valor={fmt(d.notas.T1)} rodape={`curso ${d.cursoAws}`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo={`Itens com pelo menos ${Math.round((d.corteAws ?? 0.7) * 100)}%`}
            valor={`${d.itensT1.filter((i) => (i.aluno ?? 0) >= (d.corteAws ?? 0.7) - 1e-9).length}/${d.itensT1.length}`}
            rodape="cada um vale 10 na T1; abaixo do mínimo vale 0"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Média da turma na T1"
            valor={fmt(t1?.media)}
            rodape={
              t1?.percentil != null
                ? `você está acima de ${Math.round(t1.percentil)}% dela`
                : undefined
            }
          />
        </Grid>
      </Grid>
      <AvisoAws d={d} />
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            Item por item
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A barra é a sua nota em cada item (em % do máximo); o traço é a média da turma. Como no
            AWS Academy, o item com pelo menos {Math.round((d.corteAws ?? 0.7) * 100)}% conta como
            aprovado e vale 10; abaixo disso vale 0. A T1 é a média dos itens.
          </Typography>
          <BarrasComparacao
            rotulo="Itens da T1"
            linhas={d.itensT1.map((i) => ({
              id: i.id,
              rotulo: i.nome,
              voce: i.aluno ?? 0,
              turma: i.mediaTurma,
              detalhe: `${(i.aluno ?? 0) >= (d.corteAws ?? 0.7) - 1e-9 ? 'aprovado: vale 10' : `abaixo de ${Math.round((d.corteAws ?? 0.7) * 100)}%: vale 0`} · ${fmtPct(i.concluiramTurma)} da turma fez este item`,
            }))}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}

/** Explica o que aconteceu com a conta do AWS Academy deste aluno, quando há algo a explicar. */
function AvisoAws({ d }: { d: DisciplinaDesempenho }) {
  const ident = d.awsIdentificadoPor ?? '';
  const nenhum = !d.itensT1.some((i) => (i.aluno ?? 0) > 0);
  if (ident.startsWith('conta do curso')) {
    return (
      <Alert severity="info">
        As suas atividades da T1 estão em outro curso do AWS Academy, e a T1 foi calculada com os
        itens de lá. Por isso os itens abaixo aparecem sem nota.
      </Alert>
    );
  }
  if (!ident) {
    return (
      <Alert severity="warning">
        Não encontrei a sua conta no relatório do AWS Academy desta turma, então a T1 ficou 0. Se
        você fez as atividades, fale com o professor e diga com qual e-mail entrou no AWS Academy.
      </Alert>
    );
  }
  if (nenhum) {
    return (
      <Alert severity="warning">
        A sua conta do AWS Academy não tem nenhuma atividade da T1 registrada no relatório (por isso
        0% em todos os itens). Se você fez as atividades com outra conta, fale com o professor. Para
        a T2, conclua os itens dentro do curso desta turma.
      </Alert>
    );
  }
  if (ident.startsWith('e-mail')) {
    return (
      <Alert severity="info">
        No AWS Academy você aparece só pelo e-mail (o nome completo não foi configurado). A sua T1
        foi considerada normalmente; para a T2, configure o nome em Account → Settings.
      </Alert>
    );
  }
  return null;
}

function Prova({ d }: { d: DisciplinaDesempenho }) {
  if (!d.grupo) {
    return <Alert severity="warning">Não encontrei entrega do seu grupo na P1.</Alert>;
  }
  const g = d.grupo;
  const gru = d.turmaInfo.componentes.GRU1;
  return (
    <Stack spacing={3}>
      {g.foraDoPrazo && (
        <Alert severity="error">
          Entrega fora do prazo: a nota do grupo registrada é 0. A correção abaixo (
          {fmt(g.notaCorrecao)}) fica só como referência.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Nota do grupo"
            valor={fmt(d.notas.GRU1)}
            rodape={gru ? `média da turma ${fmt(gru.media)}` : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Sua nota individual"
            valor={fmt(d.notas.IND1)}
            rodape="apresentação e perguntas em aula"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Destaque
            rotulo="Grupos na turma"
            valor={d.turmaInfo.grupos}
            rodape={`${d.turmaInfo.entregaram} de ${d.turmaInfo.alunos} alunos entregaram no prazo`}
          />
        </Grid>
      </Grid>
      {g.repositorio && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <GitHub fontSize="small" />
          <Link
            href={g.repositorio}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ wordBreak: 'break-all' }}
          >
            {g.repositorio}
          </Link>
          <Chip
            size="small"
            label={`${g.integrantes} integrante${g.integrantes === 1 ? '' : 's'}`}
          />
        </Stack>
      )}
      {!d.correcaoAprovada && (
        <Alert severity="info">
          A correção detalhada do seu grupo ainda não foi liberada pelo professor. A nota do grupo
          já vale.
        </Alert>
      )}
      {d.correcaoAprovada && d.requisitos.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2">
              Seu grupo em cada item da prova
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A barra é a nota do seu grupo no item (em % do peso); o traço é a média dos grupos da
              turma.
            </Typography>
            <BarrasComparacao
              rotulo="Itens da P1"
              linhas={d.requisitos.map((r) => ({
                id: r.id,
                rotulo: r.nome,
                voce: r.peso && r.grupo?.nota != null ? r.grupo.nota / r.peso : null,
                turma: r.mediaTurma,
                detalhe: r.peso ? `vale ${fmt(r.peso)} ponto(s)` : undefined,
              }))}
            />
          </CardContent>
        </Card>
      )}
      {d.correcaoAprovada && d.requisitos.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2">
              De onde veio a nota do grupo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Quanto cada item vale, quanto o grupo ganhou, o que ficou de fora e por quê.
            </Typography>
            <TabelaOrigem
              foraDoPrazo={g.foraDoPrazo}
              linhas={d.requisitos.map((r) => ({
                id: r.id,
                nome: r.nome,
                secao: r.secao,
                peso: r.peso,
                nota: r.grupo?.nota ?? null,
                status: r.grupo?.status ?? null,
                justificativa: r.grupo?.justificativa ?? null,
              }))}
            />
          </CardContent>
        </Card>
      )}
      {d.criterios && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
              Critérios da correção
            </Typography>
            <CriteriosView c={d.criterios} />
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

function Calculo({ d }: { d: DisciplinaDesempenho }) {
  const composta = d.p3 === 'composta';
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" component="h2">
            Critérios de {d.nome}
          </Typography>
          <Typography>
            <strong>P1</strong> = 0,3 × T1 + 0,5 × individual + 0,2 × grupo
            <br />
            <strong>P2</strong> = 0,3 × T2 + 0,5 × individual + 0,2 × grupo
            <br />
            <strong>P3</strong> ={' '}
            {composta
              ? '0,3 × exercícios do semestre + 0,7 × projeto individual em aula'
              : 'projeto individual apresentado em aula (0 a 10)'}
          </Typography>
          <Typography>
            <strong>T1 e T2</strong> vêm do AWS Academy ({d.cursoAws}): cada teste ou laboratório
            dos módulos da etapa em que você atingiu pelo menos{' '}
            {Math.round((d.corteAws ?? 0.7) * 100)}% (o mínimo do AWS Academy) vale 10, abaixo disso
            vale 0, e a nota é a média dos itens. T1 encerrou em 20/09; T2 vai até 15/11, 23h59.
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 3,
              bgcolor: 'action.hover',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
            }}
          >
            {`Se frequência ≥ 75%
    MF = (P1 + P2) / 2
    Se MF ≥ 6 → aprovado
    Senão
        MF = (maior entre P1 e P2 + P3) / 2
        Se MF ≥ 6 → aprovado
        Senão → reprovado
Senão → reprovado`}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Arredondamento: só quem fez a P3 e ficou com pelo menos 5,75 tem a nota arredondada para
            6. A frequência não aparece aqui: confira no SIGA.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Semestre já encerrado (importado só com P1, P2 e P3). */
function Encerrada({
  d,
  voltar,
  semestre,
  vendoComo,
}: {
  d: DisciplinaDesempenho;
  voltar: string;
  semestre: string | null;
  vendoComo: string | null;
}) {
  const p3 = d.notas.P3 || null;
  const sit = situacao({ P1: d.notas.P1, P2: d.notas.P2, P3: p3 });
  const comp = d.turmaInfo.componentes;
  const linhas = (['P1', 'P2', 'P3'] as const)
    .filter((c) => comp[c])
    .map((c) => ({ rotulo: c, estat: comp[c]! }));
  return (
    <>
      {vendoComo && <AvisoVendoComo nome={vendoComo} />}
      <Button
        component={RouterLink}
        to={voltar}
        startIcon={<ArrowBackRounded />}
        sx={{ mb: 1, ml: -1 }}
      >
        Todas as disciplinas
      </Button>
      <Typography variant="overline" color="text.secondary" component="p">
        {d.codigo} · {d.turma} · {d.periodo} ·{' '}
        {semestre ? `${semestre.slice(0, 4)}/${semestre.slice(4)}` : ''}
      </Typography>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {d.nome}
      </Typography>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {(['P1', 'P2', 'P3'] as const).map((c) => (
            <Grid key={c} size={{ xs: 6, md: 3 }}>
              <Destaque
                rotulo={c}
                valor={fmt(d.notas[c], 1)}
                rodape={comp[c] ? `média da turma ${fmt(comp[c]!.media, 1)}` : undefined}
              />
            </Grid>
          ))}
          <Grid size={{ xs: 6, md: 3 }}>
            <Destaque
              rotulo="Média"
              valor={fmt(sit.mf, 1)}
              rodape={
                sit.tipo === 'aprovado'
                  ? 'aprovado'
                  : sit.tipo === 'p3'
                    ? 'sem P3 registrada'
                    : 'abaixo de 6'
              }
            />
          </Grid>
        </Grid>
        {linhas.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2">
                Você e a turma
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                O ponto é você; a faixa cinza é onde ficou a metade do meio da turma; o traço é a
                mediana.
              </Typography>
              <FaixasTurma linhas={linhas} />
            </CardContent>
          </Card>
        )}
        <Alert severity="info">
          Semestre encerrado: aqui aparecem só as notas das provas, como foram lançadas. A média e a
          situação oficiais (com a frequência) estão no SIGA.
        </Alert>
      </Stack>
    </>
  );
}
